/**
 * Account (settings pocket spec §6): the plan statement, then the name,
 * email, password, time zone, card and invoices, and after a gap Sign out and
 * Delete account. Choices save at once; the name saves itself.
 */
import { useCallback, useEffect, useState } from "react";
import {
  getUser,
  resendVerificationEmail,
  updateUser,
  type User,
} from "@/api/auth";
import {
  getInvoices,
  getPaymentMethods,
  type Invoice,
  type PaymentMethod,
} from "@/api/billing";
import type { ApiError } from "@/api/client";
import {
  Button,
  PocketRow,
  PocketSection,
  PocketStatement,
  SelectField,
} from "@/components/ui";
import { useAuth } from "@/hooks/use-auth";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsAccountMessages } from "@/i18n/messages/settings-account";
import { AccountPlanBlock } from "./account-plan-block";
import { useSubscriptionStatus } from "./account-plan-state";
import { TIMEZONES } from "./profile-timezones";
import { useSavedTextField } from "./settings-draft-state";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError, SettingsInlineText } from "./settings-text-field";

function TrailingText({ text }: { text: string }) {
  return (
    <span className="block max-w-40 truncate" title={text}>
      {text}
    </span>
  );
}

export function AccountPage() {
  const t = useT(settingsAccountMessages);
  const common = useT(commonMessages);
  const nav = useSettingsNav();
  const { user: authenticatedUser, refreshUser, logout } = useAuth();
  const {
    status,
    failed: planFailed,
    reload: reloadPlan,
    setStatus,
  } = useSubscriptionStatus();
  const [user, setUser] = useState<User | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [card, setCard] = useState<PaymentMethod | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [linkSent, setLinkSent] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [billingFailed, setBillingFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setUser(null);
    setLinkSent(false);
    setLoadFailed(false);
    getUser().then(
      (loaded) => {
        if (!active) return;
        setUser(loaded);
      },
      () => {
        if (active) setLoadFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [loadAttempt, authenticatedUser?.id]);

  const billingCustomer = Boolean(status?.has_billing_customer);
  useEffect(() => {
    if (!billingCustomer) {
      setCard(null);
      setInvoices([]);
      return;
    }
    let active = true;
    setBillingFailed(false);
    void Promise.all([getPaymentMethods(), getInvoices(50)])
      .then(([cards, loaded]) => {
        if (!active) return;
        setCard(cards[0] ?? null);
        setInvoices(loaded);
      })
      .catch(() => {
        if (active) setBillingFailed(true);
      });
    return () => {
      active = false;
    };
  }, [billingCustomer, loadAttempt, authenticatedUser?.id]);

  const saveUser = useCallback(
    async (patch: Parameters<typeof updateUser>[0]) => {
      setUser(await updateUser(patch));
      await refreshUser();
    },
    [refreshUser],
  );

  const name = useSavedTextField({
    draftKey: "account:name",
    saved: user?.name ?? "",
    validate: (text) => (text.trim() ? null : t("enterName")),
    save: (text) => saveUser({ name: text.trim() }),
  });

  if (loadFailed) {
    return (
      <PocketStatement
        size="panel"
        title={t("loadFailedTitle")}
        line={t("loadFailedLine")}
        testId="settings-page-account"
        action={
          <Button
            size="sm"
            onClick={() => setLoadAttempt((n) => n + 1)}
            data-testid="account-retry"
          >
            {common("retry")}
          </Button>
        }
      />
    );
  }
  if (!user) {
    return (
      <p
        className="px-2 py-3 text-appkit-small font-medium text-muted"
        data-testid="settings-page-account"
      >
        {common("loading")}
      </p>
    );
  }

  const timezones = TIMEZONES.includes(user.timezone)
    ? TIMEZONES
    : [user.timezone, ...TIMEZONES];

  async function sendLink() {
    if (!user || sendingLink) return;
    setSendingLink(true);
    setActionError(null);
    try {
      const { detail } = await resendVerificationEmail(user.email);
      if (detail === "already_verified") {
        setUser({ ...user, is_email_verified: true });
        void refreshUser();
      } else {
        setLinkSent(true);
      }
    } catch (error) {
      setActionError(
        (error as Partial<ApiError>).status === 429
          ? t("linkRecentlySent")
          : t("sendLinkFailed"),
      );
    } finally {
      setSendingLink(false);
    }
  }

  async function saveChoice(patch: Parameters<typeof updateUser>[0]) {
    setActionError(null);
    try {
      await saveUser(patch);
    } catch {
      setActionError(t("saveFailed"));
    }
  }

  return (
    <div data-testid="settings-page-account">
      <SettingsFieldError error={actionError} testId="account-action-error" />
      {planFailed ? (
        <PocketRow
          title={t("planLoadFailed")}
          trailing={<TrailingText text={common("retry")} />}
          testId="account-plan-retry"
          onSelect={() => void reloadPlan()}
        />
      ) : null}
      {status ? (
        <AccountPlanBlock status={status} onStatusChange={setStatus} />
      ) : null}
      <PocketSection testId="account-rows">
        <PocketRow
          testId="settings-row-account-name"
          title={t("name")}
          trailing={
            <SettingsInlineText
              field={name}
              ariaLabel={t("name")}
              placeholder={user.email.split("@")[0]}
              testId="account-name-input"
            />
          }
          below={
            <SettingsFieldError
              error={name.error}
              testId="account-name-error"
            />
          }
        />
        <PocketRow
          testId="settings-row-account-email"
          title={user.email}
          secondLine={user.is_email_verified ? undefined : t("notVerified")}
          status={user.is_email_verified ? undefined : { tone: "question" }}
          trailing={
            user.is_email_verified ? undefined : linkSent ? (
              <span className="text-appkit-caption text-muted">
                {t("linkSent")}
              </span>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={sendingLink}
                onClick={() => void sendLink()}
                data-testid="account-email-send-link"
              >
                {sendingLink ? t("sendingLink") : t("sendLink")}
              </Button>
            )
          }
        />
        <PocketRow
          testId="settings-row-account-password"
          title={t("changePassword")}
          chevron
          onSelect={() => nav.push({ page: "account-password" })}
        />
        <PocketRow
          testId="settings-row-account-timezone"
          title={t("timeZone")}
          trailing={
            <SelectField
              variant="value"
              value={user.timezone}
              options={timezones.map((zone) => ({ value: zone, label: zone }))}
              onChange={(zone) => void saveChoice({ timezone: zone })}
              testId="account-timezone"
              ariaLabel={t("timeZone")}
            />
          }
        />
        {billingFailed ? (
          <PocketRow
            testId="account-billing-retry"
            title={t("billingLoadFailed")}
            trailing={<TrailingText text={common("retry")} />}
            onSelect={() => setLoadAttempt((n) => n + 1)}
          />
        ) : null}
        {card ? (
          <PocketRow
            testId="settings-row-account-card"
            title={t("cardEnding", { brand: card.brand, last4: card.last4 })}
            secondLine={t("cardExpires", {
              month: String(card.exp_month).padStart(2, "0"),
              year: String(card.exp_year).slice(-2),
            })}
          />
        ) : null}
        {invoices.length > 0 ? (
          <PocketRow
            testId="settings-row-account-invoices"
            title={t("invoices")}
            trailing={String(invoices.length)}
            chevron
            onSelect={() => nav.push({ page: "account-invoices" })}
          />
        ) : null}
      </PocketSection>
      <PocketSection testId="account-exit">
        <PocketRow
          testId="settings-row-account-sign-out"
          title={t("signOut")}
          onSelect={() => void logout()}
        />
        <PocketRow
          testId="settings-row-account-delete"
          title={<span className="text-danger">{t("deleteAccount")}</span>}
          chevron
          onSelect={() => nav.push({ page: "account-delete" })}
        />
      </PocketSection>
    </div>
  );
}
